import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

type SeoType = 'website' | 'article';

interface SEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  type?: SeoType;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  tags?: string[];
}

const SITE_URL = 'https://gas2.app';
const DEFAULT_TITLE = 'Gas2 – Controle total do seu veículo';
const DEFAULT_DESCRIPTION =
  'Gas2 – Controle total do seu veículo. Registre abastecimentos, compare etanol x gasolina e acompanhe manutenções.';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const normalizePath = (value: string): string => {
  if (!value || value === '/') return '/';
  const withoutHash = value.split('#')[0];
  const [pathname, query = ''] = withoutHash.split('?');
  const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const compactPathname = normalizedPathname.replace(/\/+$/g, '');

  return query ? `${compactPathname}?${query}` : compactPathname;
};

const buildAbsoluteUrl = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const normalizedPath = normalizePath(pathOrUrl);
  return normalizedPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalizedPath}`;
};

const safeText = (value: string | undefined, fallback: string): string => {
  const normalized = value?.trim();
  return normalized ? normalized : fallback;
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalPath,
  image,
  type = 'website',
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
  tags,
}) => {
  const location = useLocation();

  const pageTitle = safeText(title, DEFAULT_TITLE);
  const pageDescription = safeText(description, DEFAULT_DESCRIPTION);
  const canonicalUrl = buildAbsoluteUrl(canonicalPath ?? `${location.pathname}${location.search}`);
  const ogImage = buildAbsoluteUrl(image ?? DEFAULT_IMAGE);
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet prioritizeSeoTags defer={false}>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@gas2app" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && <meta property="article:author" content={author} />}
      {type === 'article' && tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
    </Helmet>
  );
};

export default SEO;

